import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const industryOptions = ["Manufacturing", "Automotive", "Banking", "IT/Technology", "Retail", "Healthcare", "FMCG", "Hospitality", "Education", "Logistics", "Telecom", "Real Estate", "Consulting", "Government", "Other"];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function handleGoogleSignup() {
  const redirectUrl = window.location.origin + '/dashboard';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function SignupPage() {
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", company: "", job_title: "", industry: "", phone: "", gender: "" });
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const strength = form.password.length === 0 ? 0 : form.password.length < 6 ? 1 : form.password.length < 10 ? 2 : 3;
  const strengthColors = ["bg-gray-200", "bg-red-400", "bg-yellow-400", "bg-green-500"];
  const strengthLabels = ["", "Weak", "Medium", "Strong"];

  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) { setError("Passwords do not match"); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register({ name: form.name, email: form.email, password: form.password, company: form.company, job_title: form.job_title, industry: form.industry, phone: form.phone, gender: form.gender });
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row pt-16">
      <div className="lg:w-1/2 bg-navy flex flex-col items-center justify-center p-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(#C9A84C 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
        <div className="relative z-10 text-center">
          <Link to="/" className="inline-block mb-8">
            <span className="font-playfair text-3xl"><span className="text-white font-bold">Role</span><span className="text-gold font-bold">IQ</span></span>
          </Link>
          <p className="font-playfair text-gold italic text-lg mb-10">Map Every Role. Grow Every Professional.</p>
          <div className="space-y-4 max-w-xs mx-auto">
            {["Generate frameworks in 30 seconds", "Save and access your map history", "Join a community of HR professionals"].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="font-dm text-white/80 text-sm text-left">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="lg:w-1/2 bg-white flex items-center justify-center p-10 overflow-y-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <h1 className="font-playfair text-navy text-3xl font-bold mb-2">Create your account</h1>
          <p className="font-dm text-navy/50 mb-6">Start free — no payment required</p>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 font-dm text-sm px-4 py-3 rounded-lg mb-4">{error}</div>}

          {/* Google Sign Up */}
          <button onClick={handleGoogleSignup} data-testid="google-signup-btn"
            className="w-full flex items-center justify-center gap-3 border border-navy/20 rounded-lg py-3 mb-4 hover:bg-gray-50 transition-colors">
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.6 33.4 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.2-2.7-.4-3.9z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.5 15.5 18.8 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.2 26.7 36 24 36c-5.2 0-9.6-3.5-11.1-8.2l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.7 39.4 44 34 44 24c0-1.3-.2-2.7-.4-3.9z"/></svg>
            <span className="font-dm text-navy text-sm font-medium">Continue with Google</span>
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-navy/10" />
            <span className="font-dm text-navy/40 text-xs">or sign up with email</span>
            <div className="flex-1 h-px bg-navy/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="signup-form">
            <input required placeholder="Full Name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="signup-name"
              className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
            <input required type="email" placeholder="Work Email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="signup-email"
              className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
            <div>
              <input required type="password" placeholder="Password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="signup-password"
                className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
              {form.password && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full transition-all ${strengthColors[strength]}`} style={{ width: `${(strength / 3) * 100}%` }} />
                  </div>
                  <span className="font-dm text-xs text-navy/50">{strengthLabels[strength]}</span>
                </div>
              )}
            </div>
            <input required type="password" placeholder="Confirm Password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)}
              className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
            <input required placeholder="Company Name" value={form.company} onChange={(e) => set("company", e.target.value)}
              className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
            <input required placeholder="Job Title" value={form.job_title} onChange={(e) => set("job_title", e.target.value)}
              className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
            <select required value={form.industry} onChange={(e) => set("industry", e.target.value)}
              className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm text-navy/60 focus:border-gold focus:outline-none">
              <option value="">Select Industry</option>
              {industryOptions.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
            <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)}
              className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
            <select value={form.gender} onChange={(e) => set("gender", e.target.value)} data-testid="signup-gender"
              className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm text-navy/60 focus:border-gold focus:outline-none">
              <option value="">Gender (optional)</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="non-binary">Non-binary</option>
              <option value="other">Prefer not to say</option>
            </select>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 accent-gold" />
              <span className="font-dm text-navy/60 text-sm">I agree to the <a href="#" className="text-gold hover:underline">Terms of Service</a> and <a href="#" className="text-gold hover:underline">Privacy Policy</a></span>
            </label>
            <button type="submit" disabled={!terms || loading} data-testid="signup-submit"
              className="w-full bg-navy text-white font-dm font-bold py-3 rounded-lg hover:bg-gold hover:text-navy transition-colors disabled:opacity-50">
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>
          <p className="text-center mt-5 font-dm text-sm text-navy/50">
            Already have an account? <Link to="/login" className="text-gold font-semibold hover:underline">Login</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
